export interface Question {
  id: string;
  question: string;
  answer: string;
  category: string;
  status?: string;
  order?: number;
}

export interface Section {
  section: string;
  questions: Question[];
}

export interface InputQuestion {
  id: string;
  category: string;
  status: string;
  order: number;
  question: string;
  response: string;
}

// Category represents the grouped output for a section
export interface Category {
  name: string;
  order: number;
  faqs: FAQ[];
}

// FAQ describes the shape of each question in the output
export interface FAQ {
  question_text: string;
  response: string;
  status?: string;
  order: number;
}

// Result of DataManager: overall status and list of categories
export interface DataManagerResult {
  status?: string;
  categories: Category[];
}

/**
 * Transforms an array of sections into a structured result with status and categories.
 * Strips HTML from answers, assigns orders, and retains all sections (including those without FAQs).
 * @param sections Array of Section objects
 * @param status    Global status for each FAQ (e.g. "draft" | "published")
 * @returns         DataManagerResult
 */
export const DataManager = (
  sections?: Section[],
  status?: string,
): DataManagerResult => {
  // Normalize input
  const items: Section[] = Array.isArray(sections) ? sections : [];

  // Build categories
  const categories: Category[] = items.map((sec, catIndex) => {
    // Transform each question into FAQ
    const faqs: FAQ[] = Array.isArray(sec.questions)
      ? sec.questions.map((q, faqIndex) => {
          // Keep HTML formatting in answer
          const response = typeof q.answer === "string" ? q.answer.trim() : "";

          // Determine order: prefer input order, otherwise index
          const faqOrder = typeof q.order === "number" ? q.order : faqIndex + 1;

          return {
            question_text: q.question,
            response,
            status: status ?? q.status,
            order: faqOrder,
          };
        })
      : [];

    return {
      name: sec.section,
      order: catIndex + 1,
      faqs,
    };
  });

  return {
    status,
    categories,
  };
};
